'use client';

import Image from 'next/image';
import { useParams, useRouter } from 'next/navigation';
import { Tooltip } from '@mui/material';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarLeftExpand } from 'react-icons/tb';
import intalentColor from '../../../../public/images/intalent_color.png';
import intalentLogoTextBlack from '../../../../public/images/intalent_logo_text_black.png';
import intalentLogoTextWhite from '../../../../public/images/intalent_logo_text_white.png';
import { useUserContext } from '@/app/context/UserContext';
import { useColorMode } from '@/hooks';
import { DEFAULT_PRIMARY_ROLE_PRIORITY, getPrimaryAppRole } from '@/utils/app-roles.util';
import { SIDEBAR_CSS_TRANSITION, SIDEBAR_MOTION_TRANSITION } from './AppLayout.motion';

/** Full wordmark display width; icon crop matches the mark in the text logos (~20% of width). */
const LOGO_FULL_WIDTH = 130;
const LOGO_ICON_WIDTH = 30;
const LOGO_ICON_TEXT_GAP = 6;
const LOGO_TEXT_WIDTH = LOGO_FULL_WIDTH - LOGO_ICON_WIDTH;
const LOGO_EXPANDED_WIDTH = LOGO_ICON_WIDTH + LOGO_ICON_TEXT_GAP + LOGO_TEXT_WIDTH;
const SIDEBAR_HEADER_TOP = 8;
const SIDEBAR_HEADER_STACK_GAP = 16;
const SIDEBAR_TOGGLE_SIZE = 36;
const SIDEBAR_HEADER_EXPANDED_HEIGHT = SIDEBAR_HEADER_TOP + LOGO_ICON_WIDTH;
const SIDEBAR_HEADER_COLLAPSED_HEIGHT = SIDEBAR_HEADER_TOP + LOGO_ICON_WIDTH + SIDEBAR_HEADER_STACK_GAP + SIDEBAR_TOGGLE_SIZE;
export const AppLayoutLogo = ({
  expanded,
  onToggleExpanded
}) => {
  const router = useRouter();
  const {
    companySlug
  } = useParams();
  const {
    user,
    roles,
    isUserLoading
  } = useUserContext();
  const {
    isDark
  } = useColorMode();
  const role = getPrimaryAppRole(roles ?? [], DEFAULT_PRIMARY_ROLE_PRIORITY) ?? 'External';
  const handleLogoClick = () => {
    if (!isUserLoading && user && companySlug !== 'undefined') {
      if (!['Manager', 'Recruiter', 'Admin', 'HRHead'].includes(role)) {
        return router.replace(`/${companySlug}/recruitment/prot/referrals`);
      }
      router.replace(`/${companySlug}/recruitment/dashboard`);
    } else if (companySlug === 'undefined') {
      return router.replace(`/404`);
    } else {
      return router.replace(`/`);
    }
  };
  return <motion.div className="relative w-full py-2" initial={false} animate={{
    height: expanded ? SIDEBAR_HEADER_EXPANDED_HEIGHT : SIDEBAR_HEADER_COLLAPSED_HEIGHT
  }} transition={SIDEBAR_MOTION_TRANSITION}>
            {/* Clickable inTalent logo */}
            <motion.div className="absolute" initial={false} animate={expanded ? {
      top: SIDEBAR_HEADER_TOP,
      left: 0,
      x: 0
    } : {
      top: SIDEBAR_HEADER_TOP,
      left: '50%',
      x: '-50%'
    }} transition={SIDEBAR_MOTION_TRANSITION}>
                <button type="button" onClick={handleLogoClick} className="cursor-pointer flex items-center shrink-0 overflow-hidden">
                    <motion.div className="relative flex items-center min-h-[30px] overflow-hidden" initial={false} animate={{
          width: expanded ? LOGO_EXPANDED_WIDTH : LOGO_ICON_WIDTH
        }} transition={expanded ? {
          type: 'spring',
          stiffness: 400,
          damping: 32
        } : SIDEBAR_MOTION_TRANSITION}>
                        {/* Color icon — always visible; stays put while wordmark reveals */}
                        <Image src={intalentColor} alt="Intalent Logo" className="relative z-10 shrink-0" style={{
            width: LOGO_ICON_WIDTH,
            height: 'auto',
            objectFit: 'contain'
          }} priority />
                        {/* Theme wordmark — end-anchored so .ai exits/enters the icon first */}
                        <motion.div className="flex items-center justify-end overflow-hidden shrink-0" initial={false} animate={{
            width: expanded ? LOGO_ICON_TEXT_GAP + LOGO_TEXT_WIDTH : 0
          }} transition={SIDEBAR_MOTION_TRANSITION}>
                            <div className="flex items-center shrink-0" style={{
              width: LOGO_ICON_TEXT_GAP + LOGO_TEXT_WIDTH
            }}>
                                <div style={{
                width: LOGO_ICON_TEXT_GAP,
                flexShrink: 0
              }} />
                                <div className="overflow-hidden" style={{
                width: LOGO_TEXT_WIDTH,
                flexShrink: 0
              }}>
                                    <div style={{
                  width: LOGO_FULL_WIDTH,
                  marginLeft: -LOGO_ICON_WIDTH
                }}>
                                        <Image src={isDark ? intalentLogoTextWhite : intalentLogoTextBlack} alt="" aria-hidden style={{
                    width: LOGO_FULL_WIDTH,
                    height: 'auto',
                    maxWidth: 'none',
                    objectFit: 'contain'
                  }} priority />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </button>
            </motion.div>

            {/* Expand/Collapse button for sidebar */}
            <motion.div className="absolute" initial={false} animate={expanded ? {
      top: SIDEBAR_HEADER_TOP,
      left: '100%',
      x: '-100%'
    } : {
      top: SIDEBAR_HEADER_TOP + LOGO_ICON_WIDTH + SIDEBAR_HEADER_STACK_GAP,
      left: '50%',
      x: '-50%'
    }} transition={SIDEBAR_MOTION_TRANSITION}>
                <Tooltip title={expanded ? 'Collapse Sidebar' : 'Expand Sidebar'} placement="right" arrow>
                    <button type="button" aria-label={expanded ? 'Collapse Sidebar' : 'Expand Sidebar'} aria-expanded={expanded} onClick={onToggleExpanded} className={clsx('cursor-pointer text-foreground-muted rounded-lg transition-[padding]', SIDEBAR_CSS_TRANSITION, 'hover:bg-primary/10', expanded ? 'p-1' : 'p-2')}>
                        {expanded ? <TbLayoutSidebarLeftCollapse className="text-xl" /> : <TbLayoutSidebarLeftExpand className="text-xl" />}
                    </button>
                </Tooltip>
            </motion.div>
        </motion.div>;
};